import PropTypes from 'prop-types';
import React from 'react';
import { VaButton } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  formatDate,
  isChampvaProviderClaim,
} from '~/platform/shared/claims-appeals';

import { pluralize } from '~/platform/utilities/ui';
import * as TrackedItem from '../../utils/trackedItemContent';
import { getTrackedItemDisplayNameFromEvidenceSubmission } from '../../utils/helpers';
import { useIncrementalReveal } from '../../hooks/useIncrementalReveal';
import { ANCHOR_LINKS } from '../../constants';
import DocumentCard from '../DocumentCard';

const NEED_ITEMS_STATUS = 'NEEDED_FROM_';
const CHAMPVA_FORM_FILE_NAME_PATTERN = 'vha_10_10d(?:_2027)?';
const GENERATED_CHAMPVA_SUPPORTING_DOC_PATTERN = new RegExp(
  `_${CHAMPVA_FORM_FILE_NAME_PATTERN}_supporting_doc-\\d+\\.pdf$`,
  'i',
);
const GENERATED_CHAMPVA_FORM_PATTERN = new RegExp(
  `^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}_${CHAMPVA_FORM_FILE_NAME_PATTERN}\\.pdf$`,
  'i',
);

const getEvidenceValue = (submission, camelKey, snakeKey) =>
  submission?.[camelKey] ?? submission?.[snakeKey];

const getUploadStatus = submission =>
  getEvidenceValue(submission, 'uploadStatus', 'upload_status');

// Renders the "For [Applicant]" line on a file card. A CHAMPVA application
// covers multiple applicants, so each uploaded file needs attribution.
// BE contract pending: the applicant name is attached per file upload by a
// pending BE PR (confirmed Aug 5). The response key is not pinned yet, so if
// it lands as something other than applicant_name, update it here. Until then
// this returns null and DocumentCard omits the line.
const getApplicantText = submission => {
  const applicantName = getEvidenceValue(
    submission,
    'applicantName',
    'applicant_name',
  );

  if (!applicantName || typeof applicantName !== 'string') {
    return null;
  }

  const trimmedName = applicantName.trim();
  if (!trimmedName) {
    return null;
  }

  return /^for\s+/i.test(trimmedName) ? trimmedName : `For ${trimmedName}`;
};

const isGeneratedChampvaSupportingDoc = fileName =>
  GENERATED_CHAMPVA_SUPPORTING_DOC_PATTERN.test(fileName || '');

const isGeneratedChampvaForm = fileName =>
  GENERATED_CHAMPVA_FORM_PATTERN.test(fileName || '');

const normalizeEvidenceSubmission = submission => ({
  ...submission,
  applicantText: getApplicantText(submission),
  createdAt: getEvidenceValue(submission, 'createdAt', 'created_at'),
  documentType: getEvidenceValue(submission, 'documentType', 'document_type'),
  fileName: getEvidenceValue(submission, 'fileName', 'file_name'),
  trackedItemDisplayName: getEvidenceValue(
    submission,
    'trackedItemDisplayName',
    'tracked_item_display_name',
  ),
  trackedItemFriendlyName: getEvidenceValue(
    submission,
    'trackedItemFriendlyName',
    'tracked_item_friendly_name',
  ),
  trackedItemId: getEvidenceValue(
    submission,
    'trackedItemId',
    'tracked_item_id',
  ),
  uploadStatus: getUploadStatus(submission),
});

const getTrackedItemText = item => {
  if (item.status === 'INITIAL_REVIEW_COMPLETE' || item.status === 'ACCEPTED') {
    return 'Reviewed by VA';
  }
  if (item.status === 'NO_LONGER_REQUIRED' && item.closedDate !== null) {
    return 'No longer needed';
  }
  if (item.status === 'SUBMITTED_AWAITING_REVIEW') {
    return 'Pending review';
  }
  return null;
};

const generateDocsFiled = (docsFiled, hideGeneratedChampvaSupportingDocs) => {
  return docsFiled
    .flatMap(document => {
      if (document.id && document.status) {
        const requestTypeDisplayName =
          TrackedItem.getTrackedItemDisplayFromSupportingDocument(document);
        const requestTypeText =
          document.status === 'NO_LONGER_REQUIRED'
            ? `We received this file for a closed evidence request: ${requestTypeDisplayName}`
            : `Submitted in response to request: ${requestTypeDisplayName}`;

        // If tracked item has no documents, return single item
        if (document.documents.length === 0) {
          return {
            requestTypeText,
            document: null,
            text: getTrackedItemText(document),
            date: document.date,
            type: 'tracked_item',
          };
        }
        // Split tracked item into separate items for each document
        return document.documents.map(doc => ({
          requestTypeText,
          document: doc,
          text: getTrackedItemText(document),
          date: doc.uploadDate || document.date,
          type: 'tracked_item',
        }));
      }
      return {
        requestTypeText: 'You submitted this file as additional evidence.',
        document: {
          originalFileName: document.originalFileName,
          documentTypeLabel: document.documentTypeLabel,
          uploadDate: document.date,
        },
        forText: getApplicantText(document),
        text: null,
        date: document.date,
        type: 'additional_evidence_item',
      };
    })
    .filter(item => {
      const fileName = item?.document?.originalFileName;
      return (
        !hideGeneratedChampvaSupportingDocs ||
        !isGeneratedChampvaSupportingDoc(fileName)
      );
    });
};

const successfulEvidenceSubmissionsToReceivedItems = (
  evidenceSubmissions = [],
  evidenceSubmittedText = null,
  hideGeneratedChampvaSupportingDocs = false,
) => {
  return evidenceSubmissions
    .map(normalizeEvidenceSubmission)
    .filter(es => es.uploadStatus === 'SUCCESS')
    .filter(
      es =>
        !hideGeneratedChampvaSupportingDocs ||
        !isGeneratedChampvaSupportingDoc(es.fileName),
    )
    .map(es => {
      const requestType = getTrackedItemDisplayNameFromEvidenceSubmission(es);
      const requestTypeText = requestType
        ? `Submitted in response to request: ${requestType}`
        : evidenceSubmittedText ||
          'You submitted this file as additional evidence.';

      return {
        requestTypeText,
        document: {
          originalFileName: es.fileName,
          documentTypeLabel: es.documentType,
          uploadDate: es.createdAt,
        },
        forText: es.applicantText,
        text: null,
        date: es.createdAt,
        type: 'additional_evidence_item',
      };
    });
};

const sortReceivedItems = items => {
  return items.sort((item1, item2) => {
    return new Date(item2.date) - new Date(item1.date);
  });
};

const getStatusBadgeText = item => {
  if (item.type === 'additional_evidence_item') {
    return 'On file';
  }
  if (item.text) {
    if (item.text.includes('Reviewed')) {
      return 'Reviewed by VA';
    }
    if (item.text === 'No longer needed') {
      return 'On file';
    }
    return item.text;
  }
  return null;
};

const getStatusBadgeStatus = item => {
  if (item.type === 'additional_evidence_item') {
    return 'success';
  }
  if (item.text) {
    if (item.text.includes('Reviewed') || item.text === 'No longer needed') {
      return 'success';
    }
    return 'info';
  }
  return undefined;
};

const FilesReceived = ({
  claim,
  title = 'Files received',
  description,
  emptyStateText = 'We haven’t received any files yet.',
  hideGeneratedChampvaForm = false,
  variant = 'card',
}) => {
  const {
    supportingDocuments = [],
    trackedItems = [],
    evidenceSubmissions = [],
  } = claim.attributes;

  const evidenceSubmittedText =
    claim?.attributes?.claimStatusMeta?.files?.evidenceSubmittedText || null;
  const filesReceivedDescription =
    description ||
    claim?.attributes?.claimStatusMeta?.files?.filesReceivedDescription ||
    'Files we received after you submitted them using this tool or the VA: Health and Benefits mobile app. Files submitted by mail or in person, by you or by others, don\u2019t appear in this tool.';

  // Get itemsFiled from trackedItems and supportingDocuments
  const itemsFiled = trackedItems.filter(
    item => !item.status.startsWith(NEED_ITEMS_STATUS),
  );
  itemsFiled.push(...supportingDocuments);
  const isChampvaClaim = isChampvaProviderClaim(claim);
  const receivedItems = generateDocsFiled(itemsFiled, isChampvaClaim);
  receivedItems.push(
    ...successfulEvidenceSubmissionsToReceivedItems(
      evidenceSubmissions,
      evidenceSubmittedText,
      isChampvaClaim,
    ),
  );

  const visibleReceivedItems = hideGeneratedChampvaForm
    ? receivedItems.filter(
        item => !isGeneratedChampvaForm(item?.document?.originalFileName),
      )
    : receivedItems;
  const allItems = sortReceivedItems(visibleReceivedItems);

  const {
    currentPageItems,
    shouldShowButton,
    nextBatchSize,
    onShowMoreClicked,
    setHeadingRef,
    getHeadingProps,
  } = useIncrementalReveal(allItems);

  return (
    <div className="files-received-container" data-testid="files-received">
      <h3
        id={ANCHOR_LINKS.filesReceived}
        className="vads-u-margin-top--0 vads-u-margin-bottom--3"
      >
        {title}
      </h3>
      <p>{filesReceivedDescription}</p>
      <div data-testid="files-received-cards">
        {currentPageItems.length === 0 ? (
          <div>
            <p>{emptyStateText}</p>
          </div>
        ) : (
          <>
            {/* add explicit role=list to expose the <ul> as a list for Safari/VoiceOver */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul
              className={
                variant === 'simple'
                  ? 'vads-u-padding-x--0 usa-unstyled-list'
                  : 'usa-card-group vads-u-padding-x--0 usa-unstyled-list'
              }
              role="list"
            >
              {currentPageItems.map((item, itemIndex) => {
                const statusBadgeText = getStatusBadgeText(item);
                const statusBadgeStatus = getStatusBadgeStatus(item);
                const { document } = item;
                const fileName = document?.originalFileName || null;
                const documentType = document?.documentTypeLabel || null;
                const date = document?.uploadDate || item.date;

                if (variant === 'simple') {
                  return (
                    <li
                      key={itemIndex}
                      className="vads-u-margin-bottom--2 vads-u-padding-bottom--1"
                    >
                      <h4
                        {...getHeadingProps(itemIndex)}
                        className="vads-u-margin-y--0"
                        {...(fileName && {
                          'data-dd-privacy': 'mask',
                          'data-dd-action-name': 'document filename',
                        })}
                      >
                        {fileName || 'File name unknown'}
                      </h4>
                      {documentType && (
                        <p className="vads-u-margin-top--0p5 vads-u-margin-bottom--0">
                          Document type: {documentType}
                        </p>
                      )}
                      {date && (
                        <p className="vads-u-margin-top--0p5 vads-u-margin-bottom--0">
                          Received on {formatDate(date)}
                        </p>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={itemIndex}>
                    <DocumentCard
                      index={itemIndex}
                      variant="received"
                      statusBadgeStatus={statusBadgeStatus}
                      statusBadgeText={statusBadgeText}
                      headingRef={setHeadingRef(itemIndex)}
                      fileName={fileName}
                      forText={item.forText}
                      documentType={documentType}
                      requestTypeText={item.requestTypeText}
                      date={date}
                    />
                  </li>
                );
              })}
            </ul>
            {shouldShowButton && (
              <div className="vads-u-margin-top--2">
                <VaButton
                  secondary
                  onClick={onShowMoreClicked}
                  text={`Show ${nextBatchSize} more ${pluralize(
                    nextBatchSize,
                    'file',
                    'files',
                  )}`}
                  data-testid="show-more-button"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

FilesReceived.propTypes = {
  claim: PropTypes.object.isRequired,
  description: PropTypes.string,
  emptyStateText: PropTypes.string,
  hideGeneratedChampvaForm: PropTypes.bool,
  title: PropTypes.string,
  // 'card' (default) renders the DocumentCard per file (claim files behavior).
  // 'simple' renders a flat text list (SC files tab per Figma).
  variant: PropTypes.oneOf(['card', 'simple']),
};

export default FilesReceived;
